import React, {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';

interface DisabledTabTooltipProps {
  children: React.ReactNode;
  content: string;
  disabled: boolean;
  className?: string;
}

interface TooltipPosition {
  left: number;
  top: number;
  width: number;
  placement: 'top' | 'bottom';
}

const TOOLTIP_MAX_WIDTH = 256;
const VIEWPORT_GUTTER = 16;
const TOOLTIP_GAP = 8;

const DisabledTabTooltip: React.FC<DisabledTabTooltipProps> = ({
  children,
  content,
  disabled,
  className = '',
}) => {
  const tooltipId = useId();
  const triggerRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [isPinned, setIsPinned] = useState(false);
  const [position, setPosition] = useState<TooltipPosition | null>(null);

  const updatePosition = useCallback(() => {
    if (!triggerRef.current) return;

    const rect = triggerRef.current.getBoundingClientRect();
    const width = Math.min(
      TOOLTIP_MAX_WIDTH,
      Math.max(0, window.innerWidth - VIEWPORT_GUTTER * 2),
    );
    const halfWidth = width / 2;
    const left = Math.min(
      Math.max(rect.left + rect.width / 2, VIEWPORT_GUTTER + halfWidth),
      window.innerWidth - VIEWPORT_GUTTER - halfWidth,
    );

    setPosition({
      left,
      top: rect.bottom + TOOLTIP_GAP,
      width,
      placement: 'bottom',
    });
  }, []);

  const showTooltip = useCallback(() => {
    if (!disabled) return;
    updatePosition();
    setIsOpen(true);
  }, [disabled, updatePosition]);

  const closeTooltip = useCallback(() => {
    setIsOpen(false);
    setIsPinned(false);
  }, []);

  useLayoutEffect(() => {
    if (!isOpen || !position || !tooltipRef.current || !triggerRef.current) return;

    const triggerRect = triggerRef.current.getBoundingClientRect();
    const tooltipHeight = tooltipRef.current.offsetHeight;
    const hasRoomBelow =
      triggerRect.bottom + TOOLTIP_GAP + tooltipHeight <=
      window.innerHeight - VIEWPORT_GUTTER;

    const nextPlacement = hasRoomBelow ? 'bottom' : 'top';
    const nextTop = hasRoomBelow
      ? triggerRect.bottom + TOOLTIP_GAP
      : Math.max(
          VIEWPORT_GUTTER,
          triggerRect.top - tooltipHeight - TOOLTIP_GAP,
        );

    if (position.top !== nextTop || position.placement !== nextPlacement) {
      setPosition((current) =>
        current
          ? { ...current, top: nextTop, placement: nextPlacement }
          : current,
      );
    }
  }, [isOpen, position]);

  useEffect(() => {
    if (!isOpen) return;

    const handleOutsidePointer = (event: PointerEvent) => {
      if (!triggerRef.current?.contains(event.target as Node)) {
        closeTooltip();
      }
    };
    const handleViewportChange = () => closeTooltip();
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') closeTooltip();
    };

    document.addEventListener('pointerdown', handleOutsidePointer);
    document.addEventListener('keydown', handleEscape);
    window.addEventListener('resize', handleViewportChange);
    window.addEventListener('scroll', handleViewportChange, true);

    return () => {
      document.removeEventListener('pointerdown', handleOutsidePointer);
      document.removeEventListener('keydown', handleEscape);
      window.removeEventListener('resize', handleViewportChange);
      window.removeEventListener('scroll', handleViewportChange, true);
    };
  }, [closeTooltip, isOpen]);

  useEffect(() => {
    if (!disabled) closeTooltip();
  }, [closeTooltip, disabled]);

  const togglePinnedTooltip = (event: React.MouseEvent<HTMLDivElement>) => {
    if (!disabled) return;
    event.preventDefault();
    event.stopPropagation();
    updatePosition();
    setIsPinned((current) => {
      const next = !current;
      setIsOpen(next);
      return next;
    });
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (!disabled || (event.key !== 'Enter' && event.key !== ' ')) return;
    event.preventDefault();
    updatePosition();
    setIsPinned((current) => {
      const next = !current;
      setIsOpen(next);
      return next;
    });
  };

  return (
    <div
      ref={triggerRef}
      className={`inline-flex ${disabled ? 'cursor-help' : ''} ${className}`}
      tabIndex={disabled ? 0 : undefined}
      aria-describedby={disabled && isOpen ? tooltipId : undefined}
      onMouseEnter={showTooltip}
      onMouseLeave={() => {
        if (!isPinned) setIsOpen(false);
      }}
      onFocus={showTooltip}
      onBlur={() => {
        if (!isPinned) setIsOpen(false);
      }}
      onClick={togglePinnedTooltip}
      onKeyDown={handleKeyDown}
    >
      <div className={disabled ? 'pointer-events-none' : ''}>{children}</div>
      {disabled && isOpen && position &&
        createPortal(
          <div
            ref={tooltipRef}
            id={tooltipId}
            role="tooltip"
            className="pointer-events-none fixed z-[100] -translate-x-1/2 rounded-lg bg-gray-900 px-3 py-2 text-center text-xs text-white shadow-lg"
            style={{
              left: position.left,
              top: position.top,
              width: position.width,
            }}
          >
            {content}
            <span
              className={`absolute left-1/2 -translate-x-1/2 border-[6px] border-transparent ${
                position.placement === 'bottom'
                  ? 'bottom-full border-b-gray-900'
                  : 'top-full border-t-gray-900'
              }`}
            />
          </div>,
          document.body,
        )}
    </div>
  );
};

export default DisabledTabTooltip;
